/**
 * lib/calculator-howto-data.ts
 *
 * Central registry of HowTo structured data steps for all 31 calculators.
 * Maps calculator slugs to specific task names, descriptions, and structural steps.
 *
 * Last verified: July 1, 2026
 */

export interface HowToStepMeta {
  name: string;
  text: string;
}

export interface HowToMeta {
  name: string;
  description: string;
  steps: HowToStepMeta[];
}

export const CALCULATOR_HOWTOS: Record<string, HowToMeta> = {
  concrete: {
    name: "How to Calculate Concrete Volume and Bags",
    description: "Learn how to estimate the cubic yards and standard bags of concrete needed for slabs, footings, and columns.",
    steps: [
      {
        name: "Measure your Project Area",
        text: "Measure the exact length, width, and thickness of your slabs, or the height and diameter of your circular columns in feet or inches."
      },
      {
        name: "Select Concrete Shape",
        text: "Choose whether you are pouring a rectangular slab, a footing strip, or post holes to use the correct volume formulas."
      },
      {
        name: "Calculate Volume & Bag Counts",
        text: "Input your measurements and select your dry mix bag size (typically 80 lb, 60 lb, or 40 lb bags) to compute total volume."
      },
      {
        name: "Order Materials with Waste Buffer",
        text: "Review your cubic yards and bag count results, incorporating a standard 10% waste buffer to account for subgrade deviations."
      }
    ]
  },
  paint: {
    name: "How to Estimate Room Paint Gallons",
    description: "Learn how to calculate the precise volume of paint needed for your walls and ceilings, deducting doors and windows.",
    steps: [
      {
        name: "Measure Wall and Ceiling Areas",
        text: "Measure the length, width, and height of the room, plus ceiling dimensions to calculate raw wall surface area."
      },
      {
        name: "Account for Doors and Windows",
        text: "Count the number of doors and windows in the room to subtract their non-paintable surface area from your calculations."
      },
      {
        name: "Calculate Paint Gallons",
        text: "Select single or double coats of paint to determine the exact gallons of paint required to cover the remaining walls."
      },
      {
        name: "Select Paint Finish",
        text: "Purchase paint in matching sheens (matte, satin, eggshell, or semi-gloss) suitable for the room's moisture levels."
      }
    ]
  },
  flooring: {
    name: "How to Calculate Flooring Material and Box Counts",
    description: "Learn how to measure a room for LVP, hardwood, laminate, or tile flooring and calculate carton quantities.",
    steps: [
      {
        name: "Measure Room Square Footage",
        text: "Measure the length and width of the room in feet to calculate the total base surface area."
      },
      {
        name: "Define Flooring Type & Lay Pattern",
        text: "Select LVP, wood, laminate, or tile, and pick standard straight, diagonal, or herringbone patterns."
      },
      {
        name: "Calculate Material & Waste",
        text: "Calculate the total area, adding a 10% waste buffer for straight lay patterns or 15% for diagonal installations."
      },
      {
        name: "Determine Box Counts to Purchase",
        text: "Convert your total square footage into exact box counts based on your chosen flooring manufacturer specs."
      }
    ]
  },
  area: {
    name: "How to Calculate Room Square Footage",
    description: "Step-by-step guide to calculating the square footage, square yards, and square meters of regular and irregular rooms.",
    steps: [
      {
        name: "Deconstruct Room Shapes",
        text: "Break down the physical room space into simple geometric shapes like rectangles, triangles, and circles."
      },
      {
        name: "Measure Side Dimensions",
        text: "Measure the length, width, radius, or base and height of each segmented section in feet or meters."
      },
      {
        name: "Calculate Sectional Area",
        text: "Multiply length by width for rectangles, or apply Heron's formula for irregular triangular sections."
      },
      {
        name: "Sum Total Square Footage",
        text: "Add all section measurements together to calculate the total square footage for material planning or real estate listings."
      }
    ]
  },
  fence: {
    name: "How to Estimate Fence Building Materials",
    description: "Learn how to calculate wood posts, rails, pickets, and setting concrete bags needed to construct a privacy fence.",
    steps: [
      {
        name: "Measure Total Fence Perimeter",
        text: "Measure the total linear footage of the property line where the fence will be installed."
      },
      {
        name: "Select Fence Height & Post Spacing",
        text: "Choose standard post spacing (6 ft or 8 ft) and the desired height of privacy panels."
      },
      {
        name: "Calculate Lumber Elements",
        text: "Compute the total posts, rails, pickets, and setting concrete bags needed based on picket spacing."
      },
      {
        name: "Purchase Lumber with Overage",
        text: "Order your lumber bill of materials, ensuring AWPA ground-contact post specifications are met."
      }
    ]
  },
  mulch: {
    name: "How to Estimate Landscaping Mulch and Gravel",
    description: "Learn how to calculate bulk cubic yards or standard bag counts of mulch, gravel, or topsoil for garden beds.",
    steps: [
      {
        name: "Measure Landscaping Beds",
        text: "Measure the length and width of garden beds, or radius for circular tree surrounds."
      },
      {
        name: "Define Bed Depth",
        text: "Choose the target mulch or gravel thickness, typically 2 to 3 inches for plants."
      },
      {
        name: "Calculate Cubic Yards & Bags",
        text: "Run the calculator to compute bulk volume or the equivalent counts of 2 cubic feet bags."
      },
      {
        name: "Prepare Bed & Apply Materials",
        text: "Clear weeds, install landscape fabric if using gravel, and apply the estimated material volume."
      }
    ]
  },
  drywall: {
    name: "How to Estimate Drywall Sheets and Joint Compound",
    description: "Learn how to calculate the 4x8 or 4x12 drywall sheets, tape, and joint compound needed for walls and ceilings.",
    steps: [
      {
        name: "Measure Walls and Ceilings",
        text: "Measure the height and length of all walls, plus ceiling dimensions to compute raw surface areas."
      },
      {
        name: "Deduct Openings & Select Panel Size",
        text: "Subtract door/window square footage and choose 4x8 or 4x12 drywall sheets."
      },
      {
        name: "Calculate Drywall Sheets & Supplies",
        text: "Determine total sheet count, joint compound gallons, and rolls of paper tape."
      },
      {
        name: "Plan Hanging & Finishing Scopes",
        text: "Hang the boards, tape joints, and apply compound layers up to the target level."
      }
    ]
  },
  tile: {
    name: "How to Calculate Wall or Floor Tile Requirements",
    description: "Step-by-step guide to calculating tile count, box count, grout bags, and mortar bags needed for installation.",
    steps: [
      {
        name: "Measure Surface Square Footage",
        text: "Measure the width and height of the backsplash, floor, or shower wall area."
      },
      {
        name: "Select Tile Size & Joint Spacing",
        text: "Choose your tile dimensions and the target grout line width."
      },
      {
        name: "Calculate Tiles, Grout, & Thinset",
        text: "Input measurements to estimate total tiles (with 10-15% waste), grout bags, and mortar."
      },
      {
        name: "Prep Subfloor & Lay Tiles",
        text: "Ensure substrate flatness meets TCNA tolerances before setting tiles with thinset."
      }
    ]
  },
  solar: {
    name: "How to Calculate Home Solar Costs and Payback",
    description: "Learn how to size your solar system based on monthly energy usage and calculate the financial return timeline.",
    steps: [
      {
        name: "Analyze Monthly Electric Bills",
        text: "Collect your average monthly electricity costs and local utility net-metering rules."
      },
      {
        name: "Determine Solar Panel Sizing",
        text: "Select panel wattage (typically 350-500W) and target solar coverage percentage."
      },
      {
        name: "Calculate System Cost & Payback",
        text: "Run the model to estimate total cost, federal tax credits (30%), and yearly savings."
      },
      {
        name: "Audit Roof Readiness",
        text: "Verify roof age has at least 10 years of remaining lifespan before system installation."
      }
    ]
  },
  hvac: {
    name: "How to Size and Estimate HVAC Replacement Costs",
    description: "Step-by-step guide to sizing residential heating and cooling tonnage and estimating installation costs.",
    steps: [
      {
        name: "Measure Total Conditioned Area",
        text: "Calculate the total square footage of the rooms requiring heating and cooling."
      },
      {
        name: "Select Target System Efficiency",
        text: "Choose your SEER2 efficiency rating (minimum 15 SEER2 for Southern US)."
      },
      {
        name: "Calculate Tonnage Load & Cost",
        text: "Run the load calculation to estimate required compressor tonnage and installed cost."
      },
      {
        name: "Verify Bid Scope with Contractor",
        text: "Compare contractor estimates against our model, ensuring ACCA Manual J calculations are run."
      }
    ]
  },
  roofing: {
    name: "How to Estimate Roof Replacement Costs",
    description: "Learn how to estimate roof shingle cost by footprint, eave overhangs, roof pitch, and material choice.",
    steps: [
      {
        name: "Measure Footprint & Pitch",
        text: "Measure home ground area footprint, eave overhangs, and vertical roof pitch angle."
      },
      {
        name: "Select Roof Material",
        text: "Choose asphalt shingle types, metal standing seam, clay tiles, or slate."
      },
      {
        name: "Calculate Roofing Squares",
        text: "Run the calculator to compute total squares (1 square = 100 sq ft) and estimated costs."
      },
      {
        name: "Compare Contractor Scopes",
        text: "Review quotes, ensuring they cover tear-off, underlayment rolls, and drip edge trims."
      }
    ]
  },
  kitchen: {
    name: "How to Plan a Kitchen Remodel Budget",
    description: "Learn how to structure kitchen renovation costs based on NKBA home value guidelines.",
    steps: [
      {
        name: "Determine Kitchen Square Footage",
        text: "Measure the wall-to-wall footprint of the kitchen space."
      },
      {
        name: "Define Project Scope & Finishes",
        text: "Select cabinet refacing or replacement, countertop materials, and appliance packages."
      },
      {
        name: "Calculate Remodeling Budget",
        text: "Compute your target kitchen budget based on home value (NKBA 5% to 15% rule)."
      },
      {
        name: "Layout Zones & Hire Professionals",
        text: "Finalize layout zones matching classic work triangles before ordering fabrication."
      }
    ]
  },
  bathroom: {
    name: "How to Estimate Bathroom Renovation Costs",
    description: "Step-by-step guide to calculating fixture, plumbing, labor, and compliance costs for a bathroom remodel.",
    steps: [
      {
        name: "Measure Bathroom Footprint",
        text: "Measure the length and width of the bathroom walls."
      },
      {
        name: "Select Fixture Upgrades",
        text: "Choose tub-to-shower conversions, toilet efficiency ratings, custom tiling, and vanity counts."
      },
      {
        name: "Estimate Renovation Costs",
        text: "Run the calculator to determine target hardware, labor, and permit budgets."
      },
      {
        name: "Ensure Ventilation & GFCI Compliance",
        text: "Plan exhaust fan flow rates (50 CFM min) and GFCI circuit layouts."
      }
    ]
  },
  windows: {
    name: "How to Calculate Window Replacement Costs",
    description: "Learn how to estimate window material and installation costs based on size, frame, and glass packages.",
    steps: [
      {
        name: "Count & Measure Existing Windows",
        text: "Count all windows by size, listing heights and widths of rough openings."
      },
      {
        name: "Select Frame Material & Glass",
        text: "Choose vinyl, wood, or fiberglass frames with double or triple-pane glass."
      },
      {
        name: "Calculate Replacement Costs",
        text: "Input dimensions to calculate total materials, labor, and local permit estimates."
      },
      {
        name: "Verify NFRC energy ratings",
        text: "Purchase windows meeting U-factor guidelines (U-value <= 0.22 for Northern climates)."
      }
    ]
  },
  "water-heater": {
    name: "How to Size and Estimate Water Heater Replacement",
    description: "Step-by-step guide to sizing electric, gas, or hybrid heat pump water heaters for home installations.",
    steps: [
      {
        name: "Select Fuel Type & Tank Style",
        text: "Choose between gas, electric, or hybrid heat pump, and tank vs. tankless."
      },
      {
        name: "Determine Household Hot Water Demand",
        text: "Input bedroom and bathroom counts to estimate peak shower and appliance usage."
      },
      {
        name: "Calculate Sizing & Cost",
        text: "Determine first-hour ratings (for tanks) or gallons-per-minute flow rates (for tankless)."
      },
      {
        name: "Apply Tax Credits & Local Rebates",
        text: "Apply the federal 30% tax credit (IRC 25C) and calculate local utility rebates."
      }
    ]
  },
  "roof-shingles": {
    name: "How to Calculate Shingle Bundles and Squares",
    description: "Learn how to estimate how many squares and bundles of shingles you need for your roof project.",
    steps: [
      {
        name: "Measure Roof Sections",
        text: "Measure the length and width of all sloped roof planes."
      },
      {
        name: "Select Shingle Bundle Types",
        text: "Choose standard 3-tab or architectural dimensional shingle products."
      },
      {
        name: "Calculate Squares & Bundles",
        text: "Estimate total squares, bundles (3 bundles per square), starter shingles, and underlayment."
      },
      {
        name: "Purchase Single Manufacturing Batch",
        text: "Order the calculated counts, plus 10% waste, in one color batch to prevent shade mismatch."
      }
    ]
  },
  siding: {
    name: "How to Estimate Siding Replacement Costs",
    description: "Step-by-step guide to calculating exterior wall cladding replacement budgets by material and complexity.",
    steps: [
      {
        name: "Measure Exterior Wall Area",
        text: "Measure wall lengths, heights, and gables, excluding doors and windows."
      },
      {
        name: "Select Siding Material",
        text: "Choose vinyl siding, wood boards, fiber cement, or aluminum panels."
      },
      {
        name: "Calculate Replacement Budgets",
        text: "Estimate total cost including old siding removal, house wrap, trim, and siding."
      },
      {
        name: "Verify Moisture barriers",
        text: "Ensure J-channels and self-adhering flashing are installed around wall openings."
      }
    ]
  },
  driveway: {
    name: "How to Estimate Driveway Paving Costs",
    description: "Learn how to calculate cost for asphalt, concrete, paver, or gravel driveways including excavation.",
    steps: [
      {
        name: "Measure Driveway Area",
        text: "Measure the length and width of the driveway layout."
      },
      {
        name: "Select Paving Material",
        text: "Choose asphalt paving, concrete slabs, interlocking pavers, or loose gravel."
      },
      {
        name: "Calculate Excavation & Paving Costs",
        text: "Run the calculator to compute base stone volume, paving materials, and labor."
      },
      {
        name: "Prep Subgrade & Compact Base",
        text: "Excavate loose soil, compact gravel base, and set drainage slope crowns."
      }
    ]
  },
  "home-addition": {
    name: "How to Estimate Home Addition Construction Budgets",
    description: "Step-by-step guide to calculating structural addition costs by foundation and room configuration.",
    steps: [
      {
        name: "Measure Footprint & Select Room Type",
        text: "Define room dimensions and whether you are adding a bedroom, bath, or sunroom."
      },
      {
        name: "Select Foundation & Design Scope",
        text: "Choose slab-on-grade, crawlspace, or second-story framing, and finish grades."
      },
      {
        name: "Calculate Total Addition Budgets",
        text: "Run the model to estimate structural framing, insulation, HVAC, plumbing, and permits."
      },
      {
        name: "Review IRC Compliance Requirements",
        text: "Ensure room ceiling height meets 7-foot minimums and features emergency egress openings."
      }
    ]
  },
  pool: {
    name: "How to Estimate Swimming Pool Installation Costs",
    description: "Learn how to calculate pool installation budgets including shell, excavation, heater, and safety barriers.",
    steps: [
      {
        name: "Measure Yard Clearance footprint",
        text: "Measure available space, setback constraints, and overhead utilities."
      },
      {
        name: "Select Pool Shell Material",
        text: "Choose above-ground vinyl, or inground vinyl-liner, fiberglass, or concrete gunite."
      },
      {
        name: "Calculate Installation Costs",
        text: "Estimate pool equipment, excavation, heater, decking, and electrical hookups."
      },
      {
        name: "Install Safety Enclosures",
        text: "Install mandatory 4-foot safety fences with self-closing latches per CPSC guidelines."
      }
    ]
  },
  countertop: {
    name: "How to Calculate Countertop Replacement Costs",
    description: "Learn how to measure countertops and estimate costs for quartz, granite, or marble installations.",
    steps: [
      {
        name: "Measure Countertop Run Dimensions",
        text: "Measure countertop lengths, depths, and island surface areas."
      },
      {
        name: "Select Countertop Material",
        text: "Choose quartz slabs, granite stone, marble, laminate, or solid-surfaces."
      },
      {
        name: "Calculate Square Footage & Cutouts",
        text: "Estimate total square feet, seam locations, sink cutouts, and backsplashes."
      },
      {
        name: "Template & Fabricate Offsite",
        text: "Secure cabinets level before professional templating, fabrication, and installation."
      }
    ]
  },
  gutters: {
    name: "How to Estimate Gutter Installation Costs",
    description: "Step-by-step guide to calculating gutter runs, downspout points, leaf guards, and fascia boards.",
    steps: [
      {
        name: "Measure Roof Eave Lengths",
        text: "Measure eave runs, downspout points, and count inside/outside miter joints."
      },
      {
        name: "Select Gutter Size & Profile",
        text: "Choose 5-inch or 6-inch seamless gutters in aluminum, copper, or steel."
      },
      {
        name: "Calculate Materials & Labor",
        text: "Estimate gutter runs, downspout drop lengths, hangers, and leaf guard screens."
      },
      {
        name: "Fix Fascia Boards & Slope Gutters",
        text: "Repair dry-rotted fascia boards and slope gutters 1/4 inch per 10 feet toward downspouts."
      }
    ]
  },
  "basement-finishing": {
    name: "How to Plan a Basement Finishing Budget",
    description: "Learn how to estimate framing, moisture control, drywall, and egress windows for finished basements.",
    steps: [
      {
        name: "Measure Basement Footprint Area",
        text: "Measure the length and width of the raw basement walls."
      },
      {
        name: "Select Finish Scope Options",
        text: "Choose bathroom count, legal bedrooms, finish grade levels, and moisture barrier prep."
      },
      {
        name: "Calculate Basement Renovation Budgets",
        text: "Estimate wall framing studs, drywall sheets, vapor barriers, flooring, and permits."
      },
      {
        name: "Erect Egress windows",
        text: "Ensure bedroom additions feature emergency escape rescue wells meeting local codes."
      }
    ]
  },
  insulation: {
    name: "How to Estimate Added Attic Insulation Requirements",
    description: "Learn how to calculate required insulation depth, bags, and R-values based on your climate zone.",
    steps: [
      {
        name: "Determine Climate Zone Targets",
        text: "Identify your US climate zone to determine recommended attic R-values (R-30 to R-60)."
      },
      {
        name: "Measure Existing Attic Depth",
        text: "Measure current insulation depth to calculate existing R-value."
      },
      {
        name: "Calculate Added Material Counts",
        text: "Determine added depth, square footage, and bag counts of fiberglass or cellulose."
      },
      {
        name: "Air Seal & Blow Insulation",
        text: "Seal attic bypass holes before blowing insulation to prevent moisture migration."
      }
    ]
  },
  "cabinet-refacing": {
    name: "How to Estimate Cabinet Refacing vs. Replacement",
    description: "Step-by-step guide to calculating costs for painting, refacing, or replacing kitchen cabinetry.",
    steps: [
      {
        name: "Measure Cabinet Dimensions",
        text: "Measure cabinet linear footage, count cabinet doors, drawers, and drawer fronts."
      },
      {
        name: "Select Refresh Scope",
        text: "Choose cosmetic painting, full refacing (veneers + door replacement), or custom boxes."
      },
      {
        name: "Calculate Cabinet Renovation Budgets",
        text: "Run the calculator to estimate material and contractor installation costs."
      },
      {
        name: "Secure countertop & Level Boxes",
        text: "Protect countertops during work, ensuring old frames are structurally sound before refacing."
      }
    ]
  },
  "backup-generator": {
    name: "How to Size and Estimate Backup Generators",
    description: "Learn how to calculate emergency storm loads and estimate costs for portable vs standby systems.",
    steps: [
      {
        name: "Sum Running & Surge Watts",
        text: "List all essential storm-outage loads (AC, refrigerator, sump pump) with starting watts."
      },
      {
        name: "Select Generator Type",
        text: "Choose portable gas generators, clean inverter units, or automatic standby systems."
      },
      {
        name: "Calculate kW Capacity & Cost",
        text: "Determine target kW generator capacity and total installed or hookup cost."
      },
      {
        name: "Set Up Transfer Equipment",
        text: "Install code-compliant manual or automatic transfer switches to prevent utility backfeed."
      }
    ]
  },
  "ev-charger": {
    name: "How to Estimate EV Charger Installation Costs",
    description: "Step-by-step guide to calculating Level 2 EV charging wire runs, breakers, and permitting costs.",
    steps: [
      {
        name: "Evaluate Panel Capacity",
        text: "Inspect electrical panel amperage (100A vs 200A) to check if a service upgrade is required."
      },
      {
        name: "Measure Conduit Run Distance",
        text: "Measure path distance from electrical panel to charger installation point."
      },
      {
        name: "Calculate Charger Costs",
        text: "Estimate Level 2 charger unit, wiring run, panel breakers, and permit costs."
      },
      {
        name: "Hire Electrician to Permit & Install",
        text: "Install dedicated 240V breakers and continuous-duty chargers to NEC code."
      }
    ]
  },
  "siding-material": {
    name: "How to Estimate Siding Panels and Accessories",
    description: "Learn how to calculate siding cartons, starter strips, wrap rolls, J-channel, and trim details.",
    steps: [
      {
        name: "Measure Wall Area Footprint",
        text: "Measure wall heights and lengths, adding gables and subtracting doors/windows."
      },
      {
        name: "Choose Siding Style & Waste Factor",
        text: "Select vinyl planks, vertical boards, and waste margin (10% standard, 20% complex)."
      },
      {
        name: "Calculate Cartons & Trim Profiles",
        text: "Estimate siding squares, cartons, starter strips, J-channels, and house wrap."
      },
      {
        name: "Purchase matching Batch materials",
        text: "Order siding accessories in matching dye lots to maintain exterior aesthetics."
      }
    ]
  },
  "retaining-wall": {
    name: "How to Calculate Retaining Wall Blocks and Backfill",
    description: "Learn how to estimate blocks, aggregate base, filter fabric, and drain pipes for landscape walls.",
    steps: [
      {
        name: "Measure Wall Dimensions",
        text: "Measure length and desired vertical height of the retaining wall."
      },
      {
        name: "Select Block Material type",
        text: "Choose interlocking concrete block sizing or timber retaining walls."
      },
      {
        name: "Calculate Blocks & Backfill Gravel",
        text: "Estimate block counts, capstones, drainage pipe runs, and aggregate backfill volume."
      },
      {
        name: "Excavate & Lay Base Course",
        text: "Excavate base trench, level compacted gravel foundation, and lay drain pipes behind the wall."
      }
    ]
  },
  deck: {
    name: "How to Size and Estimate Deck Framing and Materials",
    description: "Step-by-step guide to calculating footings, framing posts, joists, and decking layout dimensions.",
    steps: [
      {
        name: "Define Deck Dimensions",
        text: "Measure length and width of deck layout, listing height above grade."
      },
      {
        name: "Select Framing & Decking lumber",
        text: "Choose composite boards (Trex, Fiberon), cedar heartwood, or pressure-treated wood."
      },
      {
        name: "Calculate Framing & Footing counts",
        text: "Estimate posts, joist spacing, ledger attachments, footings, and rail materials."
      },
      {
        name: "Dig Footings below Frost line",
        text: "Dig foundation post holes below local frost depth lines to prevent shifting."
      }
    ]
  },
  roi: {
    name: "How to Calculate Home Remodeling Return on Investment",
    description: "Step-by-step guide to assessing cost recovery percentages and NAR Joy Scores for home renovations.",
    steps: [
      {
        name: "Select Renovation Project",
        text: "Choose kitchen remodel, bathroom refresh, attic conversion, roof replacement, or windows."
      },
      {
        name: "Enter estimated Project cost",
        text: "Input the total contractor quote or DIY material budget."
      },
      {
        name: "Calculate resale Value Return",
        text: "Run the ROI calculation based on NAR/NARI national average recovery indexes."
      },
      {
        name: "Assess resale Timeline & Joy Score",
        text: "Compare financial ROI margins with the subjective Joy Score (1-10) for daily life."
      }
    ]
  }
};
